import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { useRestaurant } from '../components/RestaurantProvider/RestaurantProvider'
import { useFlag } from '../components/FeatureFlag/FeatureFlag'
import { useDiningOptions, DINING_OPTION_BEHAVIORS } from './dining-options'
import { useOutpostRestaurant } from '../components/OutpostProvider/OutpostProvider'
import { FULFILLMENT_TYPES } from '../components/FulfillmentProvider/initial-fulfillment-state'

import { useAnalytics } from '../components/AnalyticsProvider/AnalyticsProvider'

const { TAKE_OUT, DELIVERY } = DINING_OPTION_BEHAVIORS
const { ASAP, FUTURE } = FULFILLMENT_TYPES

const AvailabilityContext = React.createContext({})

export const AvailabilityProvider = ({ children }) => {
  const deliveryEnabled = useFlag('oo-v3-delivery')
  const { restaurantInfo } = useRestaurant()
  const { addEventProperties, track } = useAnalytics()
  const { isOutpost, outpostOrderableNow } = useOutpostRestaurant()
  const { loading: diningOptionsLoading, data: diningOptionsData, error: diningOptionsError } = useDiningOptions({ networkOnly: false })
  const { loading: restaurantLoading, data: restaurantData, error: restaurantError } = restaurantInfo

  const context = useMemo(() => {
    if (restaurantLoading ||
      diningOptionsLoading ||
      !restaurantData ||
      !diningOptionsData
    ) {
      return {
        loading: restaurantLoading || diningOptionsLoading,
        error: restaurantError || diningOptionsError
      }
    }
    if (isOutpost) {
      const orderingAvailable = Boolean(restaurantData.restaurant.schedule.asapAvailableForTakeout && outpostOrderableNow)
      return {
        orderingAvailable,
        availability: {
          [ASAP]: {[TAKE_OUT]: orderingAvailable},
          [FUTURE]: {}
        },
        futureDatesAvailable: false,
        loading: restaurantLoading,
        error: restaurantError
      }
    }
    if (deliveryEnabled) {
      const { diningOptions } = diningOptionsData
      const filteredOptions = diningOptions
        .filter(({ behavior }) => behavior === TAKE_OUT || behavior === DELIVERY)

      const asapMap = filteredOptions
        .reduce((acc, { asapSchedule, behavior }) => ({
          ...acc,
          [behavior]: asapSchedule.availableNow
        }), {})

      const asapAvailable = Boolean(
        asapMap.TAKE_OUT ||
        asapMap.DELIVERY
      )

      const futureMap = filteredOptions
        .reduce((acc, { futureSchedule, behavior }) => {
          const { dates } = futureSchedule
          const available = dates
            .find(({timesAndGaps = []}) => timesAndGaps.length >= 1)
          return {
            ...acc,
            [behavior]: Boolean(available)
          }
        }, false)

      const futureDatesAvailable = Boolean(
        futureMap.TAKE_OUT ||
        futureMap.DELIVERY
      )

      const orderingAvailable = Boolean(asapAvailable || futureDatesAvailable)

      return {
        orderingAvailable,
        availability: {
          [ASAP]: asapMap,
          [FUTURE]: futureMap
        },
        asapAvailable,
        futureDatesAvailable,
        loading: diningOptionsLoading,
        error: diningOptionsError
      }
    }

    const takeoutData = !diningOptionsLoading && diningOptionsData &&
        diningOptionsData.diningOptions.find(option => option.behavior === DINING_OPTION_BEHAVIORS.TAKE_OUT)
    const futureDatesAvailable = takeoutData && Boolean(
      takeoutData.futureSchedule.dates
        .find(({timesAndGaps = []}) => timesAndGaps.length >= 1)
    )
    const orderingAvailable = Boolean(
      takeoutData &&
        (takeoutData.asapSchedule.availableNow || futureDatesAvailable)
    )

    return {
      orderingAvailable,
      futureDatesAvailable,
      loading: diningOptionsLoading,
      error: diningOptionsError
    }
  }, [
    isOutpost,
    deliveryEnabled,
    restaurantLoading,
    restaurantError,
    restaurantData,
    outpostOrderableNow,
    diningOptionsLoading,
    diningOptionsData,
    diningOptionsError
  ])

  const hasTrackedAvailability = React.useRef(false)
  // Track avaialbility
  React.useEffect(() => {
    if (context && context.orderingAvailable !== undefined) {
      // Add property for all future events
      addEventProperties({
        orderingAvailable: context.orderingAvailable
      })
      if (!hasTrackedAvailability.current) {
        hasTrackedAvailability.current = true
        // Explicitly track an event, ensuring the orderingAvailable property
        track('Ordering Availability', {
          orderingAvailable: context.orderingAvailable
        })
      }
    }
  }, [addEventProperties, context, track])

  return (
    <AvailabilityContext.Provider value={context}>
      {children}
    </AvailabilityContext.Provider>
  )
}

AvailabilityProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export const useAvailability = () => React.useContext(AvailabilityContext)
